import { useEffect, useMemo, useRef } from 'react';
import type { ReportTemplatePageMarginUnit } from '@/graphql/types';
import { isSupportedPageMarginValue, type PageMargin } from './pageSettings';

/**
 * Editor-only bridge for the margin number input, which is empty for a keystroke while the user
 * retypes it and can hold a value the margin contract rejects. Keep the preview on the last
 * supported margin instead, starting from the persisted one, so it never shows a margin that cannot
 * be saved. Page size, orientation, and unit are selects, so they always hold a valid value.
 *
 * Persisted templates and exports never reach this hook: their margin comes from the API contract
 * and must keep failing loudly when it is invalid.
 */
export function useDraftPageMargin(
    draftValue: unknown,
    draftUnit: ReportTemplatePageMarginUnit,
    persistedMargin: PageMargin
): PageMargin {
    const lastSupportedMargin = useRef<PageMargin>(persistedMargin);

    const pageMargin = useMemo(() => {
        if (!isSupportedPageMarginValue(draftValue, draftUnit)) {
            return lastSupportedMargin.current;
        }

        return { value: draftValue, unit: draftUnit };
    }, [draftValue, draftUnit]);

    useEffect(() => {
        lastSupportedMargin.current = pageMargin;
    }, [pageMargin]);

    return pageMargin;
}
